import {
  Button,
  Label,
  Alert,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
} from '@patternfly/react-core';
import {useEffect} from 'react';
import {RepositoryDetails} from 'src/resources/RepositoryResource';
import './Tags.css';
import {isNullOrUndefined} from 'src/libs/utils';
import Conditional from 'src/components/empty/Conditional';
import {useDeleteTag} from 'src/hooks/UseTags';
import {AlertDetails, AlertVariant, useUI} from 'src/contexts/UIContext';

export interface ModalOptions {
  isOpen: boolean;
  force: boolean;
}

export function DeleteModal(props: ModalProps) {
  const {
    deleteTags,
    successDeleteTags,
    errorDeleteTags,
    errorDeleteTagDetails,
  } = useDeleteTag(props.org, props.repo);
  const {addAlert} = useUI();
  const isReadonly: boolean = props.repoDetails?.state !== 'NORMAL';

  useEffect(() => {
    if (successDeleteTags) {
      props.loadTags();
      props.setModalOptions({
        force: false,
        isOpen: false,
      });
      if (!isNullOrUndefined(props.onComplete)) {
        props.onComplete();
      }
      const alert: AlertDetails = {
        variant: AlertVariant.Success,
        title: '',
      };
      switch (true) {
        case props.tags.length === 1 && props.modalOptions.force:
          alert.title = `Permanently deleted tag ${props.tags[0]} successfully`;
          break;
        case props.tags.length === 1 && !props.modalOptions.force:
          alert.title = `Deleted tag ${props.tags[0]} successfully`;
          break;
        case props.tags.length > 1 && props.modalOptions.force:
          alert.title = 'Permanently deleted tags successfully';
          break;
        case props.tags.length > 1 && !props.modalOptions.force:
          alert.title = `Deleted tags successfully`;
          break;
      }
      if (props.tags.length > 1) {
        alert.message = `Tags deleted: ${props.tags.join(', ')}`;
      }
      addAlert(alert);
    }
  }, [successDeleteTags]);

  useEffect(() => {
    if (errorDeleteTags) {
      const alert: AlertDetails = {
        variant: AlertVariant.Failure,
        title: '',
      };
      switch (true) {
        case props.tags.length === 1 && props.modalOptions.force:
          alert.title = `Could not permanently delete tag ${props.tags[0]}`;
          break;
        case props.tags.length === 1 && !props.modalOptions.force:
          alert.title = `Could not delete tag ${props.tags[0]}`;
          break;
        case props.tags.length > 1 && props.modalOptions.force:
          alert.title = 'Could not permanently delete tags';
          break;
        case props.tags.length > 1 && !props.modalOptions.force:
          alert.title = `Could not delete tags`;
          break;
      }
      alert.message = (
        <>
          {Array.from(errorDeleteTagDetails.getErrors()).map(([tag, error]) => (
            <p key={tag}>
              Could not delete tag {tag}: {error.error.message}
            </p>
          ))}
        </>
      );
      addAlert(alert);
    }
  }, [errorDeleteTags]);

  const title = props.modalOptions.force
    ? `Permanently delete the following tag(s)?`
    : `Delete the following tag(s)?`;
  return (
    <>
      <Modal
        id="tag-deletion-modal"
        isOpen={props.modalOptions.isOpen}
        key="modal"
        onClose={() => {
          props.setModalOptions((prevOptions) => ({
            force: false,
            isOpen: !prevOptions.isOpen,
          }));
        }}
        data-testid="delete-tags-modal"
        variant={ModalVariant.small}
      >
        <ModalHeader
          title={title}
          description={
            <Conditional if={props.modalOptions.force}>
              <span style={{color: 'red'}}>
                Tags deleted cannot be restored within the time machine window
                and will be immediately eligible for garbage collection.
              </span>
            </Conditional>
          }
        />
        <ModalBody>
          <Conditional if={isReadonly}>
            <Alert
              id="form-error-alert"
              isInline
              variant="danger"
              title={`Repository is currently in ${props.repoDetails?.state} state. Deletion is disabled.`}
            />
            <div className="delete-modal-readonly-alert" />
          </Conditional>
          <Conditional
            if={props.immutableTags && props.immutableTags.length > 0}
          >
            <Alert
              isInline
              variant="warning"
              title="Immutable tags will be skipped"
              data-testid="immutable-tags-warning"
            >
              The following tags are immutable and will not be deleted:{' '}
              {props.immutableTags?.join(', ')}
            </Alert>
            <div style={{marginBottom: '1rem'}} />
          </Conditional>
          {props.tags?.map((tag) => (
            <span key={tag}>
              <Label>{tag}</Label>{' '}
            </span>
          ))}
          <Conditional if={props.tags?.length > 20}>
            <div>
              <b>Note:</b> This operation can take several minutes.
            </div>
          </Conditional>
        </ModalBody>
        <ModalFooter>
          <Button
            key="cancel"
            variant="primary"
            onClick={() => {
              props.setModalOptions((prevOptions) => ({
                force: false,
                isOpen: !prevOptions.isOpen,
              }));
            }}
          >
            Cancel
          </Button>
          <Button
            key="modal-action-button"
            variant="primary"
            onClick={() =>
              deleteTags({tags: props.tags, force: props.modalOptions.force})
            }
            isDisabled={isReadonly}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

type ModalProps = {
  modalOptions: ModalOptions;
  setModalOptions: (modalOptions) => void;
  tags: string[];
  immutableTags?: string[];
  onComplete?: () => void;
  loadTags: () => void;
  org: string;
  repo: string;
  repoDetails: RepositoryDetails;
};
